import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  CheckCircle, 
  Receipt, 
  FileText,
  RefreshCw,
  Settings
} from 'lucide-react';
import PendingExpenseApprovals from './PendingExpenseApprovals';
import ExpenseApprovalModal from './ExpenseApprovalModal';
import ApprovedExpensesForInvoicing from './ApprovedExpensesForInvoicing';
import ExpenseDetailsModal from '../contractor/ExpenseDetailsModal';
import { useToast } from '@/components/ui/use-toast';
import expenseService from '@/services/expenseService';
import {
  Expense,
  ExpenseApprovalData
} from '@/types/expense';

interface ClientExpenseDashboardProps {
  contractId: string;
  contractorName?: string;
  onNavigateToInvoicing?: () => void;
  className?: string;
}

const ClientExpenseDashboard: React.FC<ClientExpenseDashboardProps> = ({
  contractId,
  contractorName,
  onNavigateToInvoicing,
  className
}) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isExpenseDetailsOpen, setIsExpenseDetailsOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [summaryData, setSummaryData] = useState({
    pendingCount: 0,
    pendingAmount: '0.00',
    approvedCount: 0,
    approvedAmount: '0.00',
    totalApprovedAmount: '0.00'
  });
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const { toast } = useToast();

  // Fetch expense summary data using the proper contractor expense summary
  const fetchExpenseSummary = async () => {
    if (!contractId) {
      setIsLoadingSummary(false);
      return;
    }

    try {
      setIsLoadingSummary(true);
      
      // Use the contractor's expense summary endpoint which correctly counts INVOICED expenses as "approved"
      const summary = await expenseService.getContractExpenseSummary(contractId);
      
      setSummaryData({
        pendingCount: summary.pendingCount, // DRAFT expenses (labeled as "Pending" in UI)
        pendingAmount: summary.pendingAmount,
        approvedCount: summary.approvedCount, // INVOICED expenses (labeled as "Approved" in UI)
        approvedAmount: summary.approvedAmount,
        totalApprovedAmount: summary.totalApprovedAmount // Total value of INVOICED expenses
      });
      
    } catch (error: any) {
      console.error('Error fetching expense summary:', error);
      // Don't show toast for summary errors, just use default values
      setSummaryData({
        pendingCount: 0,
        pendingAmount: '0.00',
        approvedCount: 0,
        approvedAmount: '0.00',
        totalApprovedAmount: '0.00'
      });
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // Load summary data when component mounts or refreshKey changes
  useEffect(() => {
    fetchExpenseSummary();
  }, [contractId, refreshKey]);

  // Handle expense actions
  const handleViewExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsExpenseDetailsOpen(true);
  };

  const handleApproveExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsApprovalModalOpen(true);
  };

  const handleRejectExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsApprovalModalOpen(true);
  };

  const handleApprovalSuccess = (expense: Expense) => {
    // Refresh the dashboard data
    setRefreshKey(prev => prev + 1);
    
    toast({
      title: 'Success',
      description: `Expense ${expense.description} has been processed`,
    });
  };



  const handleCreateInvoiceWithExpenses = (expenses: Expense[]) => {
    // In a real implementation, this would create a new invoice
    toast({
      title: 'Invoice Created',
      description: `New invoice created with ${expenses.length} expenses`,
    });
    
    // Navigate to invoicing if callback provided
    onNavigateToInvoicing?.();
    
    // Refresh data
    setRefreshKey(prev => prev + 1);
  };

  const handleRefreshAll = () => {
    setRefreshKey(prev => prev + 1);
    // Summary data will be refreshed via useEffect when refreshKey changes
    toast({
      title: 'Refreshed',
      description: 'Expense data has been updated',
    });
  };

  // Show message if no contract is available
  if (!contractId) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="text-center py-8">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
            <FileText className="h-6 w-6 text-gray-600" />
          </div>
          <h3 className="text-lg font-medium mb-1">No Contracts Available</h3>
          <p className="text-muted-foreground mb-4">
            You don't have any contracts to review expenses for yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Expense Management</h1>
          {contractorName && (
            <p className="text-muted-foreground">
              Managing expenses for {contractorName}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefreshAll}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh All
          </Button>
          {onNavigateToInvoicing && (
            <Button onClick={onNavigateToInvoicing} className="gap-2">
              <Receipt className="h-4 w-4" />
              Go to Invoicing
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            Pending Approvals
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Ready for Invoicing
          </TabsTrigger>
          <TabsTrigger value="overview" className="gap-2">
            <FileText className="h-4 w-4" />
            Overview
          </TabsTrigger>
        </TabsList>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending" className="space-y-4">
          <div className="grid gap-6">
            <PendingExpenseApprovals
              key={`pending-${refreshKey}`}
              contractId={contractId}
              onViewExpense={handleViewExpense}
              onApproveExpense={handleApproveExpense}
              onRejectExpense={handleRejectExpense}
            />
          </div>
        </TabsContent>

        {/* Ready for Invoicing Tab */}
        <TabsContent value="approved" className="space-y-4">
          <div className="grid gap-6">
            <ApprovedExpensesForInvoicing
              key={`approved-${refreshKey}`}
              contractId={contractId}
              onCreateInvoice={handleCreateInvoiceWithExpenses}
            />
          </div>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Expense Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-yellow-50 rounded-md">
                      <div className="text-2xl font-bold text-yellow-600">
                        {isLoadingSummary ? '-' : summaryData.pendingCount}
                      </div>
                      <div className="text-sm text-muted-foreground">Pending</div>
                      {!isLoadingSummary && summaryData.pendingCount > 0 && (
                        <div className="text-xs text-yellow-600 mt-1">
                          £{summaryData.pendingAmount}
                        </div>
                      )}
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-md">
                      <div className="text-2xl font-bold text-green-600">
                        {isLoadingSummary ? '-' : summaryData.approvedCount}
                      </div>
                      <div className="text-sm text-muted-foreground">Approved</div>
                      {!isLoadingSummary && summaryData.approvedCount > 0 && (
                        <div className="text-xs text-green-600 mt-1">
                          £{summaryData.approvedAmount}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-md">
                    <div className="text-2xl font-bold text-blue-600">
                      {isLoadingSummary ? '£0.00' : `£${summaryData.totalApprovedAmount}`}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Approved Expense Value</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2"
                  onClick={() => setActiveTab('pending')}
                >
                  <Clock className="h-4 w-4" />
                  Review Pending Expenses
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2"
                  onClick={() => setActiveTab('approved')}
                >
                  <CheckCircle className="h-4 w-4" />
                  Create Invoices
                </Button>
                {onNavigateToInvoicing && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={onNavigateToInvoicing}
                  >
                    <Receipt className="h-4 w-4" />
                    Create Invoice
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2"
                  onClick={handleRefreshAll}
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh Data
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recent activity to display</p>
                <p className="text-sm">
                  Expense approvals and rejections will appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ExpenseDetailsModal
        expense={selectedExpense}
        isOpen={isExpenseDetailsOpen}
        onClose={() => {
          setIsExpenseDetailsOpen(false);
          setSelectedExpense(null);
        }}
      />

      <ExpenseApprovalModal
        expense={selectedExpense}
        isOpen={isApprovalModalOpen}
        onClose={() => {
          setIsApprovalModalOpen(false);
          setSelectedExpense(null);
        }}
        onApprove={async (expenseId, data) => {
          try {
            await expenseService.approveOrRejectExpense(expenseId, data);
            handleApprovalSuccess(selectedExpense!);
          } catch (error: any) {
            toast({
              title: 'Error',
              description: 'Failed to approve expense',
              variant: 'destructive',
            });
          }
        }}
        onReject={async (expenseId, data) => {
          try {
            await expenseService.approveOrRejectExpense(expenseId, data);
            handleApprovalSuccess(selectedExpense!);
          } catch (error: any) {
            toast({
              title: 'Error',
              description: 'Failed to reject expense',
              variant: 'destructive',
            });
          }
        }}
      />
    </div>
  );
};

export default ClientExpenseDashboard; 